import clsx from 'clsx';
import styles from './styles.module.scss';
import { useOrientation } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { useFacilityStore } from '@/stores';
import type { FloorInfo } from '@/services/dto/kiosk-config';
import { FACILITY_SEARCH_MODE } from '@/enums/facility';
import { useFacilityBuildingOptions, useFacilityFloorList } from '@/hooks/poi/useFacilityData';
import ShopFnbFacilityFloorButton from '@/components/common/shop-fnb-facility/buttons/floor';
import ShopFnbFacilityBuildingDropdown from '@/components/common/shop-fnb-facility/building-dropdown';
import { AppUtils } from '@/utils/app';

const FacilitySearchByFloor = () => {
    const { t } = useTranslation();
    const { isHorizontal } = useOrientation();

    // Optimized store access
    const selectedFloor = useFacilityStore((state) => state.selectedFloor);
    const selectedBuildingId = useFacilityStore((state) => state.selectedBuildingId);
    const setSelectedFloor = useFacilityStore((state) => state.setSelectedFloor);
    const setSelectedBuildingId = useFacilityStore((state) => state.setSelectedBuildingId);
    const updateFacilityContents = useFacilityStore((state) => state.updateFacilityContents);

    // Memoized computed data
    const buildingOptions = useFacilityBuildingOptions(t);
    const floorList = useFacilityFloorList(selectedBuildingId, t);

    const handleSelectFloor = (floor: { id: string; label: string; value: string; data: FloorInfo | string[] | null }) => {
        setSelectedFloor(floor);
        updateFacilityContents(FACILITY_SEARCH_MODE.SEARCH_BY_FLOOR);
    };

    const handleSelectBuilding = (buildingId: string) => {
        setSelectedBuildingId(buildingId);
        handleSelectFloor(floorList[0]);
    };

    return (
        <div
            className={clsx(styles['search-by-floor'], {
                [styles['search-by-floor--vertical']]: !isHorizontal,
                ['ml-0! justify-center!']: buildingOptions.length === 1,
            })}
        >
            <div className={clsx(styles['search-by-floor__floors'], { [styles['search-by-floor__floors--vertical']]: !isHorizontal })} data-region-nav>
                {floorList.map((floor) => {
                    return (
                        <ShopFnbFacilityFloorButton
                            tabIndex={9}
                            data-interactive
                            role="radio"
                            type="button"
                            key={floor.id}
                            classes={floor.id === 'all' ? 'text' : 'number'}
                            aria-label={
                                floor.id === 'all'
                                    ? t('facility.search.tts.content.all_floors')
                                    : t('facility.search.tts.content.floor', { floor: AppUtils.getFloorTTSText(floor.label) })
                            }
                            title={floor.label}
                            aria-checked={floor.id === selectedFloor?.id}
                            onClick={() => handleSelectFloor(floor)}
                            isVertical={!isHorizontal}
                        />
                    );
                })}
            </div>
            {buildingOptions.length > 1 && (
                <div className={styles['search-by-floor__dropdown']}>
                    <ShopFnbFacilityBuildingDropdown defaultValue={selectedBuildingId || 'all'} options={buildingOptions} onSelect={handleSelectBuilding} />
                </div>
            )}
        </div>
    );
};

export default FacilitySearchByFloor;
